import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLocations from '@modules/organizationalStructure/locations/hooks/useLocations';

const CompanyLocationDropDowns = ({ register, setValue, data, hideCountry = false, disabled = false, onCityChange, additionalFields }) => {
    const { t } = useTranslation();

    const {
        fetchCountries,
        fetchRegionsByCountry,
        fetchSubRegionsByRegion,
        fetchCitiesbySubRegion,
        countryOptions,
        regionOptions,
        subRegionOptions,
        cityOptions,
        selectedCountry,
        selectedRegion,
        selectedSubRegion,
        selectedCity,
        setSelectedCountry,
        setSelectedRegion,
        setSelectedSubRegion,
        setSelectedCity,
    } = useLocations();

    // Set Bulgaria as default country when hideCountry is true
    useEffect(() => {
        let isMounted = true;

        const setDefaultCountry = async () => {
            if (hideCountry && !selectedCountry) {
                try {
                    await fetchCountries();
                    // Wait for countryOptions to be populated
                    if (countryOptions.length > 0 && isMounted) {
                        const bulgaria = countryOptions.find(option => option.value === 'BG');
                        if (bulgaria) {
                            setValue('country', bulgaria.value);
                            setSelectedCountry(bulgaria.value);
                            // Ensure we fetch regions after setting the country
                            await fetchRegionsByCountry(bulgaria.value);
                        }
                    }
                } catch (error) {
                    console.error('Error setting default country:', error);
                }
            }
        };

        setDefaultCountry();

        return () => {
            isMounted = false;
        };
    }, [hideCountry, countryOptions.length]);

    useEffect(() => {
        const initializeLocations = async () => {
            if (data?.cityId?.id) {
                if (!hideCountry) {
                    await fetchCountries();
                }
                
                if (data.cityId.countryId) {
                    setSelectedCountry(data.cityId.countryId);
                    setValue('country', data.cityId.countryId);
                    await fetchRegionsByCountry(data.cityId.countryId);
                }
                
                if (data.cityId.regionId) {
                    setSelectedRegion(data.cityId.regionId);
                    setValue('region', data.cityId.regionId);
                    await fetchSubRegionsByRegion(data.cityId.regionId);
                }
                
                if (data.cityId.subregionId) {
                    setSelectedSubRegion(data.cityId.subregionId);
                    setValue('subregion', data.cityId.subregionId);
                    await fetchCitiesbySubRegion(data.cityId.subregionId);
                }
                
                if (data.cityId.id) {
                    setValue('cityId', data.cityId.id);
                    setSelectedCity(data.cityId.id);
                }
            }
        };

        initializeLocations();
    }, [data]);

    return (
        <>
            <div className='flex'>
                {!hideCountry && (
                    <select
                        className="small-select"
                        name='country'
                        {...register('country')}
                        value={selectedCountry || ''}
                        onFocus={fetchCountries}
                        onChange={(e) => {
                            const value = e.target.value;
                            setValue('country', value);
                            setSelectedCountry(value);
                            setValue('region', null);
                            setValue('subregion', null);
                            setValue('cityId', "");
                            setSelectedRegion(null);
                            setSelectedSubRegion(null);
                            setSelectedCity("");
                            fetchRegionsByCountry(value);
                        }}
                        disabled={disabled}
                    >
                        <option value="">{t('nav.schools.form.country')}</option>
                        {countryOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                )}
                <select
                    className="small-select"
                    name='region'
                    {...register('region')}
                    value={selectedRegion || ''}
                    disabled={disabled || !selectedCountry}
                    onFocus={() => {
                        fetchRegionsByCountry(selectedCountry);
                    }}
                    onChange={(e) => {
                        const value = e.target.value;
                        setValue('region', value);
                        setSelectedRegion(value);
                        setValue('subregion', null);
                        setValue('cityId', "");
                        setSelectedSubRegion(null);
                        setSelectedCity("");
                        fetchSubRegionsByRegion(value);
                    }}
                >
                    <option value="">{t('persons.form.region')}</option>
                    {regionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <select
                    className="small-select"
                    name='subregion'
                    {...register('subregion')}
                    value={selectedSubRegion || ''}
                    disabled={disabled || !selectedRegion || subRegionOptions.length === 0}
                    onChange={(e) => {
                        const value = e.target.value;
                        setValue('subregion', value);
                        setSelectedSubRegion(value);
                        setValue('cityId', "");
                        setSelectedCity("");
                        fetchCitiesbySubRegion(value);
                    }}
                >
                    <option value="">{t('persons.form.municipality')}</option>
                    {subRegionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            <div className='flex' style={{ marginTop: '10px' }}>
                <select
                    className="small-select"
                    name='cityId'
                    {...register('cityId')}
                    value={selectedCity || ''}
                    disabled={disabled || !selectedSubRegion || cityOptions.length === 0}
                    onChange={(e) => {
                        const value = e.target.value;
                        setValue('cityId', value);
                        setSelectedCity(value);
                        
                        // Call the onCityChange callback with the postal code
                        const selectedOption = cityOptions.find(opt => String(opt.value) === String(value));
                        if (onCityChange && selectedOption?.postal) {
                            onCityChange(selectedOption.postal);
                        }
                    }}
                >
                    <option value="">{t('persons.form.city')}</option>
                    {cityOptions.map((option) => (
                        <option key={option.value} value={option.value} data-postal={option.postal}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {additionalFields}
            </div>
        </>
    );
};

export default CompanyLocationDropDowns;